import { ColDef } from "@ag-grid-community/core";
import LevelsCell from "../components/AdvanceDataWrangler/LevelsCell";
import { useCallback, useMemo } from "react";
import HyperLinkCell from "../components/AdvanceDataWrangler/HyperLinkCell";

type TableDefinitionProps = {
    getFieldFilters: (params: any, field: string) => void;
}

type GridOptions = {
    default: ColDef;
    columns: ColDef[];
};

/**
 * Hook that provides the ADW table definition for AG Grid
 */
export const useADWTableDefinition = (props: TableDefinitionProps): GridOptions => {

    const MAX_CHARACTERS_CELL_LENGTH = 32767;

    const objectToStringFormatter = useCallback((params: any) => {
        let value = ''
        if (params.data?.additional && params.data.additional.levels) {
            value = params.data.additional.levels;
            value = value.slice(0, MAX_CHARACTERS_CELL_LENGTH);
        }
        if (typeof value === 'object' && value !== null) {
            return JSON.stringify(value, null, 2).slice(0, MAX_CHARACTERS_CELL_LENGTH);
        }
        return value;
    }, []);

    return useMemo((): GridOptions => ({
        default: {
            resizable: true,
            wrapText: true,
            menuTabs: ['columnsMenuTab'],
            columnChooserParams: {
                contractColumnSelection: true,
                suppressSyncLayoutWithGrid: true
            },
            filter: false,
            filterParams: {
                debounceMs: 1000,
            },
            autoHeight: true,
            sortable: true
        },
        columns: [
            { field: 'id', headerName: 'id', filter: false, width: 120 },
            {
                field: 'domain', headerName: 'domain',
                filter: 'agSetColumnFilter',
                filterParams: {
                    values: (params: any) => props.getFieldFilters(params, "domain"),
                    refreshValuesOnOpen: true,
                }
            },
            {
                field: 'table_label', headerName: 'table_label', width: 200,
                filter: 'agSetColumnFilter',
                filterParams: {
                    values: (params: any) => props.getFieldFilters(params, "table_label"),
                    refreshValuesOnOpen: true,
                }
            },
            {
                field: 'source', headerName: 'source',
                filter: 'agSetColumnFilter',
                filterParams: {
                    values: (params: any) => props.getFieldFilters(params, "source"),
                    refreshValuesOnOpen: true,
                }
            },
            {
                field: 'table_name',
                headerName: 'table_name',
                width: 250,
                filter: 'agSetColumnFilter',
                filterParams: {
                    values: (params: any) => props.getFieldFilters(params, "table_name"),
                    refreshValuesOnOpen: true,
                }
            },
            {
                field: 'name', headerName: 'name',
                filter: 'agTextColumnFilter',
                filterParams: {
                    filterOptions: ["contains"],
                    maxNumConditions: 1,
                }
            },
            { field: 'label', headerName: 'description', width: 300 },
            {
                field: 'levels',
                headerName: 'levels',
                cellRenderer: LevelsCell,
                valueFormatter: objectToStringFormatter,
                sortable: false,
            },
            { field: 'type_level', headerName: 'type_level' },
            { field: 'type_data', headerName: 'type_data' },
            { field: 'type_var', headerName: 'type_var' },
            { field: 'type_field', headerName: 'type_field' },
            { field: 'order_display', headerName: 'order_display', width: 200 },
            { field: 'branching_logic', headerName: 'branching_logic' },
            { field: 'instruction', headerName: 'instruction', width: 400 },
            { field: 'identifier_columns', headerName: 'identifier_column', sortable: false },
            { field: 'order_sort', headerName: 'order_sort', sortable: false },
            { field: 'note', headerName: 'note' },
            {
                field: 'url_table_warn_data',
                headerName: 'url_table_warn_data',
                cellRenderer: HyperLinkCell,
                sortable: false
            },
            { field: 'url_warn_data', headerName: 'url_warn_data', cellRenderer: HyperLinkCell, sortable: false },
            {
                field: 'url_table_warn_use',
                headerName: 'url_table_warn_use',
                cellRenderer: HyperLinkCell,
                sortable: false
            },
            { field: 'url_warn_use', headerName: 'url_warn_use', cellRenderer: HyperLinkCell, sortable: false },
            { field: 'study', headerName: 'study' },
            { field: 'sub_domain', headerName: 'subdomain' },
            { field: 'header', headerName: 'header' },
            { field: 'unit', headerName: 'unit' },
            {
                field: 'metric', headerName: 'metric', width: 270,
                filter: 'agSetColumnFilter',
                filterParams: {
                    values: (params: any) => props.getFieldFilters(params, "metric"),
                    refreshValuesOnOpen: true,
                }
            },
            { field: 'atlas', headerName: 'atlas' },
            { field: 'label_es', headerName: 'label_es' },
            {
                field: 'levels_es',
                headerName: 'level_es',
                cellRenderer: LevelsCell,
                filterValueGetter: objectToStringFormatter,
                filterParams: {
                    values: (params: any) => {
                        const options = new Set();
                        params?.api?.forEachNode((node: any) =>
                            Object.values(node.data?.additional?.Levels || {})
                                .filter((v) => v)
                                .forEach(options.add, options)
                        );
                        params.success(options);
                    }
                },
                valueFormatter: objectToStringFormatter,
                sortable: false,
            },
            { field: 'instruction_es', headerName: 'instruction_es' },
            { field: 'header_es', headerName: 'header_es' },
            { field: 'note_es', headerName: 'note_es' },
            { field: 'url_table', headerName: 'url_table', cellRenderer: HyperLinkCell, sortable: false },
            { field: 'table_redcap', headerName: 'table_redcap', sortable: false },
            { field: 'name_redcap', headerName: 'name_redcap', sortable: false },
            { field: 'name_redcap_exp', headerName: 'name_redcap_exp', sortable: false },
            { field: 'name_deap', headerName: 'name_DEAP', sortable: false },
            { field: 'table_nda', headerName: 'table_NDA', sortable: false },
            { field: 'table_nda_5_0', headerName: 'table_NDA 5_0', sortable: false },
            { field: 'name_nda', headerName: 'name_NDA', sortable: false },
        ]
    }), [objectToStringFormatter]);
};
